import { useCallback, useEffect, useRef, useState } from 'react';

import { config } from 'config';

interface UncontrolledStateProps<T> {
  value?: undefined;
  initialValue?: T;
  onChange?: undefined;
}
interface ControlledStateProps<T> {
  value: T;
  onChange: (value: T) => void;
  initialValue?: never;
}

type UseControlledStateProps<T> =
  | UncontrolledStateProps<T>
  | ControlledStateProps<T>;

/**
 * Manages a value that can be used in either controlled or uncontrolled mode.
 *
 * Controlled mode:
 * - Pass `value` and `onChange`.
 * - The hook returns `value` and `setValue` will only call `onChange`.
 *
 * Uncontrolled mode:
 * - Omit `value` and provide `initialValue`.
 * - The hook stores internal state initialized from `initialValue`.
 *
 * Returns a tuple:
 * - `value`: resolved current value (controlled or internal)
 * - `setValue(nextValue)`: updates internal state only in uncontrolled mode and always calls `onChange`
 * - `isControlled`: whether the hook was initialized in controlled mode
 *
 * Notes:
 * - Control mode is decided on first render and should not change later.
 * - In development, switching between controlled and uncontrolled modes logs a warning.
 * - `initialValue` is only used on mount.
 * - `undefined` is used as the controlled-mode indicator (`value !== undefined`), so for empty values use `null`.
 */
export const useControlledState = <T>({
  value: controlledValue,
  initialValue,
  onChange,
}: UseControlledStateProps<T>) => {
  const [internalValue, setInternalValue] = useState<T>(initialValue as T);

  const isControlledRef = useRef(controlledValue !== undefined);
  const isControlled = isControlledRef.current;

  useEffect(() => {
    if (config.IS_DEVELOPMENT) {
      const isStillControlled = controlledValue !== undefined;

      if (isStillControlled !== isControlled) {
        console.warn(
          'useControlledState switched between controlled and uncontrolled modes. This is not supported.'
        );
      }
    }
  }, [controlledValue, isControlled]);

  const value =
    isControlled && controlledValue !== undefined
      ? controlledValue
      : internalValue;

  const setValue = useCallback(
    (newValue: T) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange]
  );

  return [value, setValue, isControlled] as const;
};
